import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { AddCircleOutlineOutlined, Close } from "@mui/icons-material";
import RichTextEditor from "../../../components/common/RichTextEditor";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";

interface ContactsNotesCardProps {
  contactId?: number | string;
}

interface ContactResponse {
  notes: string | null;
}

const ContactsNotesCard: React.FC<ContactsNotesCardProps> = ({ contactId }) => {
  const [open, setOpen] = useState(false);
  const [notesContent, setNotesContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  /** Fetch Contact Notes */
  useEffect(() => {
    const fetchNotes = async () => {
      try {
        if (!contactId) return;
        const res = await apiService.get<ContactResponse>(
          `/api/contacts/contact/${contactId}/`
        );

        setNotesContent(res.data.notes || null);
      } catch (error) {
        console.error("Failed to fetch notes:", error);
        toast.error("Failed to fetch contact notes");
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [contactId]);

  /** Save or Update Notes */
  const handleSaveNotes = async (htmlContent: string) => {
    try {
      const payload = { notes: htmlContent };

      await apiService.patch(`/api/contacts/contact/${contactId}/`, payload);

      setNotesContent(htmlContent);
      toast.success(
        notesContent ? "Notes updated successfully" : "Notes added successfully"
      );
      handleClose();
    } catch (error) {
      console.error("Failed to update notes:", error);
      toast.error("Failed to save notes. Please try again.");
    }
  };

  return (
    <Grid size={{ xs: 12, md: 3 }}>
      <Card sx={{ boxShadow: 0, height: "100%" }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Contact Notes
            </Typography>
            <IconButton onClick={handleOpen}>
              <AddCircleOutlineOutlined />
            </IconButton>
          </Box>

          <Grid container spacing={3}>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading notes...
              </Typography>
            ) : notesContent ? (
              <Grid size={{ xs: 12 }}>
                <div dangerouslySetInnerHTML={{ __html: notesContent }} />
              </Grid>
            ) : (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  No notes have been added yet.
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Dialog for Editing Notes */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DialogTitle>
            {notesContent ? "Update Contact Notes" : "Add Contact Notes"}
          </DialogTitle>
          <Close onClick={handleClose} sx={{ mr: 3, cursor: "pointer" }} />
        </Box>

        <DialogContent>
          <RichTextEditor
            initialContent={notesContent || ""}
            onSave={handleSaveNotes}
            title="Notes About Contact"
            placeholder="Add any relevant details about this contact..."
            saveButtonText={notesContent ? "Update" : "Save"}
          />
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default ContactsNotesCard;
